import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import {
  useDeleteInstructor,
  useInstructorQuery,
  useUpdateInstructor,
} from "../api/queries";
import {
  InstructorForm,
  instructorFormValuesToUpdateDTO,
} from "../ui/instructors/InstructorForm";

export function InstructorEditPage() {
  const nav = useNavigate({
    from: "/app/master/instructors/$id/edit",
  });
  const { id } = useParams({
    from: "/app/master/instructors/$id/edit",
  });
  const { data, ...result } = useInstructorQuery(id);
  const updateInstructor = useUpdateInstructor(id);
  const deleteInstructor = useDeleteInstructor();

  const instructor = data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄູສອນ", to: "/app/master/instructors" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂຄູສອນ
            </h2>
            <p className="text-muted-foreground">ປັບປຸງລາຍລະອຽດຂອງຄູສອນ.</p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await import("@devhop/ui").then((m) =>
                  m.confirm({
                    title: "ລຶບຄູສອນ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄູສອນນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: {
                      variant: "destructive",
                    },
                  }),
                );
                if (!ok) return;

                try {
                  await toast.promise(deleteInstructor.run(id), {
                    loading: "ກຳລັງລຶບ...",
                    success: () => {
                      nav({ to: "/app/master/instructors" });
                      return "ລຶບຄູສອນສຳເລັດ";
                    },
                    error: "ລຶບຄູສອນລົ້ມເຫຼວ",
                  });
                } catch (error) {
                  // Error handling is done by toast.promise
                }
              }}
            >
              ລຶບ
            </Button>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/master/instructors" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດຄູສອນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!instructor ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <InstructorForm
                initialValues={{
                  name: instructor.name,
                  phoneNumber: instructor.phoneNumber || "",
                  email: instructor.email || "",
                  bankAccountNumber: instructor.bankAccountNumber || "",
                  employmentType: instructor.employmentType,
                  monthlyFreeGeneralClassSessions:
                    instructor.monthlyFreeGeneralClassSessions,
                  privatePackagePayoutMode: instructor.privatePackagePayoutMode,
                  payoutRates: instructor.payoutRates,
                  status: instructor.status,
                  notes: instructor.notes || "",
                }}
                onSubmit={async (vals) => {
                  try {
                    await updateInstructor.mutateAsync(
                      instructorFormValuesToUpdateDTO(vals),
                    );

                    nav({ to: "/app/master/instructors" });
                  } catch (error) {
                    // Error handling is done by the mutation
                  }
                }}
                submitting={updateInstructor.isPending}
              />
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
